import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, X, Star, Pencil, Eye, EyeOff, ExternalLink, Trash2, Copy } from 'lucide-react';
import api from '../../lib/api';
import ProgramForm from '../../components/ProgramForm';
import ProgramFilters, { applyProgramFilters } from '../../components/ProgramFilters';
import ProgramTable from '../../components/ProgramTable';
import { useToast } from '../../hooks/use-toast';

const AdminPrograms = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // program | null
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [price, setPrice] = useState('');
  const [time, setTime] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try { const res = await api.get('/admin/programs'); setItems(res.data || []); }
    catch (e) { console.error('Failed to load programs:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const filtered = useMemo(() => applyProgramFilters(items, { q, type, status, price, time }), [items, q, type, status, price, time]);

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast({ title: okMsg }); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const approve = (p) => act(() => api.post(`/admin/programs/${p.id}/approve`), 'Approved');
  const reject = (p) => { const reason = window.prompt('Reason for rejection?') || ''; act(() => api.post(`/admin/programs/${p.id}/reject`, { reason }), 'Rejected'); };
  const feature = (p) => act(() => api.post(`/admin/programs/${p.id}/feature`, null, { params: { value: !p.is_featured, order: 0 } }), p.is_featured ? 'Unfeatured' : 'Featured');
  const publish = (p) => act(() => api.post(`/programs/${p.id}/publish`, null, { params: { value: !p.is_published } }), p.is_published ? 'Unpublished' : 'Published');
  const remove = (p) => { if (window.confirm('Delete this program?')) act(() => api.delete(`/programs/${p.id}`), 'Deleted'); };
  const duplicate = (p) => act(() => api.post(`/programs/${p.id}/duplicate`), 'Duplicated — an editable copy was created');
  const preview = (p) => window.open(`/programs/${p.slug}`, '_blank');

  if (editing) {
    return (
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif-display text-3xl text-burgundy">Edit Program</h1>
          <button onClick={() => setEditing(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>
        <ProgramForm program={editing} onDone={() => { setEditing(null); fetchItems(); }} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div><div className="eyebrow mb-2">Admin</div><h1 className="font-serif-display text-4xl text-burgundy">Programs <span className="font-italic-display text-rose-italic">Management</span></h1>
        <p className="mt-2 text-mahogany/80">Review, edit, publish and feature every event, retreat &amp; class.</p></div>

      <ProgramFilters q={q} setQ={setQ} type={type} setType={setType} status={status} setStatus={setStatus} price={price} setPrice={setPrice} time={time} setTime={setTime} />

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : (
          <ProgramTable rows={filtered} renderActions={(p) => (
            <>
              {(p.status === 'pending' || p.status === 'draft') && <>
                <IconBtn onClick={() => approve(p)} title="Approve" ok><Check className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => reject(p)} title="Reject" danger><X className="w-4 h-4" /></IconBtn>
              </>}
              <IconBtn onClick={() => setEditing(p)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => duplicate(p)} title="Duplicate" data-testid={`duplicate-program-${p.id}`}><Copy className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => publish(p)} title={p.is_published ? 'Unpublish' : 'Publish'}>{p.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</IconBtn>
              {p.status === 'approved' && <IconBtn onClick={() => feature(p)} title={p.is_featured ? 'Unfeature' : 'Feature on home'} active={p.is_featured}><Star className="w-4 h-4" /></IconBtn>}
              {p.is_published && p.status === 'approved' && <IconBtn onClick={() => preview(p)} title="Preview"><ExternalLink className="w-4 h-4" /></IconBtn>}
              <IconBtn onClick={() => remove(p)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>
            </>
          )} />
        )}
    </div>
  );
};

const IconBtn = ({ children, danger, ok, active, ...p }) => (
  <button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
    danger ? 'bg-red-50 text-red-600 hover:bg-red-100'
    : ok ? 'bg-green-50 text-green-700 hover:bg-green-100'
    : active ? 'bg-mahogany text-cream'
    : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>
);

export default AdminPrograms;
